import { isOnNodeJS } from "common/utils/context";
import type { TextRenderInfo } from "troika-three-text";
import { THREETextRenderer } from "./THREETextRenderer";

/**
 * Scheduler for requestAnimationFrame calls.
 */
export class RenderScheduler {
  private requestedFrame: ReturnType<typeof requestAnimationFrame> | undefined;
  private animateInternal: boolean = false;

  constructor(
    private applyAnimation: () => void,
    private render: () => void,
  ) {
    THREETextRenderer.instance.setCollectTextPromiseCallback((textRenderInfo) =>
      this.textPromises.push(textRenderInfo),
    );
  }

  public updateRequested: boolean = false;

  private textPromises: Promise<TextRenderInfo>[] = [];

  set animate(value: boolean) {
    this.animateInternal = value;
    if (value) {
      this.requestFrame();
    }
    if (!value && !this.updateRequested) {
      this.stopAnimate();
    }
  }

  get animate(): boolean {
    return this.animateInternal;
  }

  dispose() {
    this.stopAnimate();
  }

  private stopAnimate() {
    if (this.requestedFrame) {
      cancelAnimationFrame(this.requestedFrame);
      this.requestedFrame = undefined;
    }
  }

  markRendererForUpdate() {
    this.updateRequested = true;
    if (!this.requestedFrame) {
      // This will trigger rendering in case we're not animating.
      // We only schedule a frame instead of rendering right away since there might be a lot of consecutive changes and markRendererForUpdate calls.
      this.requestFrame();
    }
  }

  private requestFrame() {
    if (isOnNodeJS()) {
      return;
    }
    this.stopAnimate();

    const frame = async () => {
      if (this.textPromises.length > 0) {
        await Promise.all(this.textPromises);
        this.textPromises = [];
      }
      this.applyAnimation?.();
      this.render?.();
      this.updateRequested = false;
      if (this.animate) {
        this.requestedFrame = requestAnimationFrame(frame);
      } else {
        this.requestedFrame = undefined;
      }
    };
    this.requestedFrame = requestAnimationFrame(frame);
  }
}
